import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../api/axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const REPORTS = [
  { id: 'by-client', label: 'By Client', desc: 'Jobs breakdown per client', color: '#0f74b9' },
  { id: 'by-designer', label: 'By Designer', desc: 'Workload per designer', color: '#037b3e' },
  { id: 'by-category', label: 'By Category', desc: 'Jobs per category type', color: '#b64326' },
  { id: 'by-status', label: 'By Status', desc: 'Pipeline status overview', color: '#f9b21e' },
  { id: 'by-mode', label: 'By Mode', desc: 'Billable vs Retainer split', color: '#6f42c1' },
  { id: 'by-month', label: 'By Month', desc: 'Monthly job trend', color: '#0c5460' },
];

const KEY_MAP = {
  'by-client': { nameKey: 'client', valueKey: 'total' },
  'by-designer': { nameKey: 'designer', valueKey: 'total' },
  'by-category': { nameKey: 'category', valueKey: 'total' },
  'by-status': { nameKey: 'status', valueKey: 'total' },
  'by-mode': { nameKey: 'mode', valueKey: 'total' },
  'by-month': { nameKey: 'month', valueKey: 'total' },
};

export default function Reports() {
  const [active, setActive] = useState('by-client');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [mode, setMode] = useState('');
  const [year, setYear] = useState(new Date().getFullYear());

  const { data = [], isLoading } = useQuery({
    queryKey: ['report', active, dateFrom, dateTo, mode, year],
    queryFn: () => {
      const params = { date_from: dateFrom, date_to: dateTo };
      if (active === 'by-client') params.mode = mode;
      if (active === 'by-month') params.year = year;
      return api.get(`/reports/${active}`, { params }).then(r => r.data);
    },
  });

  const { nameKey, valueKey } = KEY_MAP[active];
  const maxVal = Math.max(...data.map(d => Number(d[valueKey]) || 0), 1);
  const activeReport = REPORTS.find(r => r.id === active);

  const totalJobs = data.reduce((s, d) => s + (Number(d.total) || 0), 0);
  const totalCompleted = data.reduce((s, d) => s + (Number(d.completed) || 0), 0);
  const totalOverdue = data.reduce((s, d) => s + (Number(d.overdue) || 0), 0);

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 18 }}>
        {REPORTS.map(r => (
          <div key={r.id} role="button" tabIndex={0} onClick={() => setActive(r.id)} onKeyDown={e => { if (e.key === 'Enter') setActive(r.id); }}
            style={{ background: '#fff', border: r.id === active ? `2px solid ${r.color}` : '1px solid #e9ecef', borderRadius: 9, padding: 16, cursor: 'pointer' }}>
            <div style={{ width: 36, height: 36, borderRadius: 8, background: r.id === active ? r.color + '22' : '#f1f3f5', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10, fontSize: 18 }}>
              {r.id === 'by-client' ? '👤' : r.id === 'by-designer' ? '🎨' : r.id === 'by-category' ? '📁' : r.id === 'by-status' ? '📊' : r.id === 'by-mode' ? '💰' : '📅'}
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#343a40', marginBottom: 3 }}>{r.label}</div>
            <div style={{ fontSize: 11, color: '#6c757d' }}>{r.desc}</div>
          </div>
        ))}
      </div>

      <div style={{ background: '#fff', border: '1px solid #e9ecef', borderRadius: 8, padding: '12px 14px', display: 'flex', alignItems: 'flex-end', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        {active !== 'by-month' && (
          <>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#495057', marginBottom: 4 }}>Date From</label>
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                style={{ padding: '6px 9px', border: '1px solid #ced4da', borderRadius: 5, fontSize: 12 }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#495057', marginBottom: 4 }}>Date To</label>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                style={{ padding: '6px 9px', border: '1px solid #ced4da', borderRadius: 5, fontSize: 12 }} />
            </div>
          </>
        )}
        {active === 'by-client' && (
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#495057', marginBottom: 4 }}>Mode</label>
            <select value={mode} onChange={e => setMode(e.target.value)}
              style={{ padding: '6px 9px', border: '1px solid #ced4da', borderRadius: 5, fontSize: 12 }}>
              <option value="">All Modes</option>
              {['Internal', 'Retainer', 'Billable', 'Non - Billable'].map(m => <option key={m}>{m}</option>)}
            </select>
          </div>
        )}
        {active === 'by-month' && (
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#495057', marginBottom: 4 }}>Year</label>
            <select value={year} onChange={e => setYear(Number(e.target.value))}
              style={{ padding: '6px 9px', border: '1px solid #ced4da', borderRadius: 5, fontSize: 12 }}>
              {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        )}
        <button type="button" className="btn btn-outline btn-sm" onClick={() => { setDateFrom(''); setDateTo(''); setMode(''); }}>Clear</button>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e9ecef', borderRadius: 9, overflow: 'hidden' }}>
        <div style={{ padding: '12px 14px', borderBottom: '1px solid #e9ecef', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#343a40' }}>{activeReport?.label} Report</span>
          <span style={{ fontSize: 12, color: '#6c757d' }}>{data.length} records</span>
        </div>

        {isLoading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#6c757d' }}>Loading…</div>
        ) : data.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#6c757d' }}>No data for selected filters.</div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, padding: 14, borderBottom: '1px solid #e9ecef' }}>
              {[
                { label: 'Total Records', val: data.length },
                { label: 'Total Jobs', val: totalJobs },
                { label: 'Completed', val: totalCompleted },
                { label: 'Overdue', val: totalOverdue },
              ].map(s => (
                <div key={s.label} style={{ background: '#f8f9fa', borderRadius: 7, padding: 11, textAlign: 'center' }}>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#343a40' }}>{s.val}</div>
                  <div style={{ fontSize: 10, color: '#6c757d', marginTop: 3 }}>{s.label}</div>
                </div>
              ))}
            </div>

            <div style={{ padding: '16px 14px 10px' }}>
              <ResponsiveContainer width="100%" height={Math.max(220, data.length * 36)}>
                <BarChart data={data} layout="vertical" margin={{ left: 20, right: 30, top: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f3f5" />
                  <XAxis type="number" tick={{ fontSize: 11, fill: '#6c757d' }} domain={[0, maxVal]} />
                  <YAxis type="category" dataKey={nameKey} tick={{ fontSize: 11, fill: '#6c757d' }} width={110} />
                  <Tooltip
                    contentStyle={{ fontSize: 12, borderRadius: 6, border: '1px solid #e9ecef' }}
                    formatter={(v) => [v, 'Jobs']} />
                  <Bar dataKey={valueKey} radius={[0, 4, 4, 0]} maxBarSize={22}>
                    {data.map((entry, i) => (
                      <Cell key={i} fill={entry.color || activeReport?.color || '#0f74b9'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div style={{ borderTop: '1px solid #e9ecef', overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: '#f8f9fa' }}>
                    <th style={{ padding: '8px 14px', textAlign: 'left', fontWeight: 600, color: '#6c757d', fontSize: 10, textTransform: 'uppercase' }}>{nameKey.charAt(0).toUpperCase() + nameKey.slice(1)}</th>
                    <th style={{ padding: '8px 14px', textAlign: 'right', fontWeight: 600, color: '#6c757d', fontSize: 10, textTransform: 'uppercase' }}>Total</th>
                    {data[0]?.completed !== undefined && <th style={{ padding: '8px 14px', textAlign: 'right', fontWeight: 600, color: '#6c757d', fontSize: 10, textTransform: 'uppercase' }}>Completed</th>}
                    {data[0]?.overdue !== undefined && <th style={{ padding: '8px 14px', textAlign: 'right', fontWeight: 600, color: '#6c757d', fontSize: 10, textTransform: 'uppercase' }}>Overdue</th>}
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, i) => (
                    <tr key={i} style={{ borderTop: '1px solid #f1f3f5' }}>
                      <td style={{ padding: '8px 14px', color: '#343a40', fontWeight: 500 }}>{row[nameKey]}</td>
                      <td style={{ padding: '8px 14px', textAlign: 'right', fontWeight: 700, color: '#0f74b9' }}>{row.total}</td>
                      {row.completed !== undefined && <td style={{ padding: '8px 14px', textAlign: 'right', color: '#037b3e' }}>{row.completed}</td>}
                      {row.overdue !== undefined && <td style={{ padding: '8px 14px', textAlign: 'right', color: '#b64326' }}>{row.overdue}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
